// frontend/src/pages/SignatureCapturePage.js
import React, { useRef, useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { obtenerTipoDispositivo } from '../utils/detector-dispositivo';
import { API_BASE_URL } from '../utils/apiConfig';
import { obtenerUbicacionAmigable } from '../utils/geoLocationService';

function obtenerNombreNavegador() {
  const userAgent = navigator.userAgent;
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) return 'Chrome';
  if (userAgent.includes('Edg')) return 'Edge';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari';
  if (userAgent.includes('Opera') || userAgent.includes('OPR')) return 'Opera';
  return 'Otro';
}

function SignatureCapturePage() {
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const hasInkRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });
  const [isDrawing, setIsDrawing] = useState(false);
  const [mode, setMode] = useState('hand');
  const [typedName, setTypedName] = useState('');
  const [ipPublica, setIpPublica] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false); // Nuevo estado para mostrar modal de éxito
  const navigate = useNavigate();
  const { id } = useParams();
  const [correo, setCorreo] = useState('');
  const location = useLocation();
  const photo = location.state?.selfie;

  // Obtener correo del paciente
  useEffect(() => {
    const obtenerCorreo = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/documento-por-cedula/${location.state?.cedula}`);
        const data = await res.json();
        if (data?.documento?.correo) {
          setCorreo(data.documento.correo);
        }
      } catch (err) {
        console.error('Error al obtener el correo del paciente:', err);
      }
    };
    if (location.state?.cedula) {
      obtenerCorreo();
    }
  }, [location.state?.cedula]);

  // Obtener IP pública
  useEffect(() => {
    fetch('https://api.ipify.org?format=json')
      .then(res => res.json())
      .then(data => setIpPublica(data.ip))
      .catch(err => console.warn('No se pudo obtener IP pública:', err));
  }, []);

  // Helper: obtener posición relativa al canvas
  const getCanvasOffset = (clientX, clientY) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  // Inicializa y configura el canvas (tamaños y estilo)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Ajuste para pantallas retina (devicePixelRatio)
    const ratio = window.devicePixelRatio || 1;
    const displayWidth = Math.min(window.innerWidth - 40, 700); // ancho visible
    const displayHeight = 200;

    canvas.style.width = `${displayWidth}px`;
    canvas.style.height = `${displayHeight}px`;
    canvas.width = Math.round(displayWidth * ratio);
    canvas.height = Math.round(displayHeight * ratio);

    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);

    // Fondo blanco para facilitar detección de "vacío"
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, displayWidth, displayHeight);

    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Agregar atributo para mejor rendimiento en getImageData
    canvas.setAttribute('willReadFrequently', 'true');

  }, [mode]);

  // Lógica de eventos con Pointer Events + prevención de gestos móviles
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Evita el comportamiento por defecto (scroll/pull-to-refresh) al tocar el canvas
    canvas.style.touchAction = 'none'; // indica al navegador que no haga gestos por defecto sobre este elemento

    // Evita pull-to-refresh/overscroll cuando el usuario dibuja (Chrome Android)
    const originalBodyOverscroll = document.body.style.overscrollBehavior;
    document.body.style.overscrollBehavior = 'none';

    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    const onPointerDown = (e) => {
      // Solo botón principal / primer touch
      if (e.button && e.button !== 0) return;
      e.preventDefault();
      canvas.setPointerCapture(e.pointerId);
      isDrawingRef.current = true;
      setIsDrawing(true);
      const { x, y } = getCanvasOffset(e.clientX, e.clientY);
      lastPointRef.current = { x, y };
      ctx.beginPath();
      ctx.moveTo(x, y);
    };

    const onPointerMove = (e) => {
      if (!isDrawingRef.current) return;
      e.preventDefault();
      const { x, y } = getCanvasOffset(e.clientX, e.clientY);
      ctx.lineTo(x, y);
      ctx.stroke();
      lastPointRef.current = { x, y };
      hasInkRef.current = true; 
    };

    const endPointer = (e) => {
      if (isDrawingRef.current) {
        try { canvas.releasePointerCapture && canvas.releasePointerCapture(e.pointerId); } catch (err) {}
        isDrawingRef.current = false;
        setIsDrawing(false);
      }
    };

    // En iOS Safari el evento touchmove suele ser 'passive' por defecto — agregamos un listener no-passive solo en canvas
    const onTouchMovePrevent = (ev) => {
      // Si hay un solo touch y estamos dibujando, bloquear scroll
      if (ev.touches && ev.touches.length === 1 && isDrawingRef.current) {
        ev.preventDefault();
      }
    };

    // Registrar listeners (raw DOM, no React synthetic events)
    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', endPointer);
    canvas.addEventListener('pointercancel', endPointer);
    // prevención adicional para touchmove con passive:false
    canvas.addEventListener('touchmove', onTouchMovePrevent, { passive: false });

    // Limpieza
    return () => {
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', endPointer);
      canvas.removeEventListener('pointercancel', endPointer);
      canvas.removeEventListener('touchmove', onTouchMovePrevent);
      document.body.style.overscrollBehavior = originalBodyOverscroll; // restaurar
    };
  }, []); // solo al montar

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    // Fondo blanco en todo el canvas (manteniendo escala previa)
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const ratio = window.devicePixelRatio || 1;
    ctx.fillStyle = 'white';
    // como el contexto está escalado, usamos dimensiones lógicas:
    ctx.fillRect(0, 0, canvas.width / ratio, canvas.height / ratio);

    // Restaurar estilo del lápiz
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    hasInkRef.current = false;
  };

  // Verifica si hay dibujo (versión mejorada)
  const canvasHasDrawing = () => {
    const canvas = canvasRef.current;
    if (!canvas) return false;
    
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    
    try {
      // Revisar más píxeles para mejor detección
      const sampleW = Math.min(w, 100); // Máximo 100 píxeles de ancho
      const sampleH = Math.min(h, 50);  // Máximo 50 píxeles de alto
      
      const imgData = ctx.getImageData(0, 0, sampleW, sampleH).data;
      
      // Contar píxeles no blancos
      let nonWhitePixels = 0;
      for (let i = 0; i < imgData.length; i += 4) {
        const r = imgData[i];
        const g = imgData[i + 1]; 
        const b = imgData[i + 2];
        const alpha = imgData[i + 3];
        
        // Si el píxel no es blanco puro (considerando transparencia)
        if (alpha > 0 && !(r === 255 && g === 255 && b === 255)) {
          nonWhitePixels++;
        }
      }
      
      // Si hay al menos 10 píxeles no blancos, consideramos que hay dibujo
      const hasDrawing = nonWhitePixels >= 10;
      
      console.log(`Píxeles no blancos detectados: ${nonWhitePixels}, ¿Hay dibujo?: ${hasDrawing}`);
      
      return hasDrawing;
      
    } catch (err) {
      console.error('Error detectando dibujo:', err);
      // Si hay error, asumir que hay dibujo para no bloquear al usuario
      return true;
    }
  };

  const handleSubmit = async () => {
    if (!photo) {
      alert('No se encontró la selfie. Debes tomarla antes de firmar.');
      return;
    }

    if (mode === 'typed' && !typedName.trim()) {
      alert('Por favor, escribe tu nombre completo para continuar.');
      return;
    }

    if (mode === 'hand') {
      if (!canvasHasDrawing()) {
        if (mode === 'hand') {
                if (!hasInkRef.current && !canvasHasDrawing()) {
        alert('Por favor, dibuja tu firma antes de continuar.');
        return;
      }
    }
  }
}

    setIsProcessing(true);
    setProcessingMessage('Procesando tu firma...');

    try {
      let signatureData;
      if (mode === 'hand') {
        signatureData = canvasRef.current.toDataURL();
      } else {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = 400;
        canvas.height = 100;
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.font = '36px "Dancing Script", cursive';
        ctx.fillStyle = 'black';
        ctx.textAlign = 'center';
        ctx.fillText(typedName, canvas.width / 2, canvas.height / 2 + 10);
        signatureData = canvas.toDataURL();
      }

      setProcessingMessage('Guardando firma y selfie...');

      const saveRes = await fetch(`${API_BASE_URL}/api/guardar-firma/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature: signatureData, selfie: photo })
      });

      if (!saveRes.ok) {
        const errorData = await saveRes.json().catch(() => ({}));
        console.error('Error al guardar firma:', errorData);
        throw new Error(errorData.message || 'Error al guardar firma');
      }

      setProcessingMessage('Verificando tu ubicación...');

      // NUEVA IMPLEMENTACIÓN CON GEOLOCALIZACIÓN HÍBRIDA
      const ubicacion = await obtenerUbicacionAmigable();
      console.log('Ubicación obtenida:', ubicacion);

      setProcessingMessage('Generando documento final...');
      const tipoDispositivo = obtenerTipoDispositivo();
      const navegador = obtenerNombreNavegador();

      const finalRes = await fetch(`${API_BASE_URL}/api/generar-pdf-final/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lat: ubicacion.lat,
          lon: ubicacion.lon,
          ip: ipPublica,
          dispositivo: tipoDispositivo,
          navegador,
          ubicacionInfo: ubicacion.mensaje
        })
      });

      if (finalRes.ok) {
        // ✅ Mostrar modal de éxito bonito en lugar del alert feo
        setIsProcessing(false);
        setShowSuccess(true);
        
        // Redirigir automáticamente después de 3 segundos
        setTimeout(() => {
          window.location.href = 'https://colombianadetrasplantes.com';
        }, 3000);
        
      } else {
        const errorData = await finalRes.json().catch(() => ({}));
        console.error('Error al generar documento final:', errorData);
        throw new Error(errorData.message || 'Error al generar documento final');
      }

    } catch (err) {
      console.error('Error general al firmar:', err);
      setIsProcessing(false);
      
      // Mensajes de error más específicos
      if (err.message.includes('410') || err.message.includes('Gone')) {
        alert('El documento ha expirado. Por favor, solicita un nuevo enlace.');
      } else if (err.message.includes('404') || err.message.includes('Not Found')) {
        alert('Documento no encontrado. Verifica el enlace.');
      } else {
        alert('Error en la conexión al servidor. Intenta nuevamente.');
      }
    }
  };

  // Popup de carga
  const LoadingPopup = () => (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.8)', display: 'flex',
      justifyContent: 'center', alignItems: 'center', zIndex: 9999
    }}>
      <div style={{
        backgroundColor: 'white', padding: '30px', borderRadius: '15px',
        textAlign: 'center', maxWidth: '320px', width: '100%'
      }}>
        <div style={{
          width: '50px', height: '50px', border: '5px solid #f3f3f3',
          borderTop: '5px solid #007bff', borderRadius: '50%',
          animation: 'spin 1s linear infinite', margin: '0 auto 20px'
        }}></div>
        <p style={{ fontWeight: 'bold', color: '#333' }}>{processingMessage}</p>
      </div>
    </div>
  );

  // ✨ Modal de éxito bonito con animaciones
  const SuccessPopup = () => (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.85)', display: 'flex',
      justifyContent: 'center', alignItems: 'center', zIndex: 9999,
      animation: 'fadeIn 0.3s ease'
    }}>
      <div style={{
        backgroundColor: 'white', padding: '40px 45px', borderRadius: '24px',
        textAlign: 'center', maxWidth: '420px', width: '90%',
        boxShadow: '0 25px 80px rgba(0,0,0,0.4)',
        animation: 'scaleIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
      }}>
        {/* Ícono de éxito animado */}
        <div style={{
          width: '90px', height: '90px', borderRadius: '50%',
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 25px',
          boxShadow: '0 15px 40px rgba(16, 185, 129, 0.4)',
          animation: 'bounceIn 0.6s ease'
        }}>
          <svg 
            width="45" 
            height="45" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="white" 
            strokeWidth="3" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <polyline 
              points="20 6 9 17 4 12" 
              style={{ 
                strokeDasharray: 30, 
                strokeDashoffset: 0,
                animation: 'drawCheck 0.6s ease 0.2s both'
              }}
            />
          </svg>
        </div>
        
        <h2 style={{ 
          color: '#1f2937', 
          fontSize: '26px', 
          fontWeight: '700', 
          marginBottom: '12px',
          margin: '0 0 12px 0'
        }}>
          ¡Documento Firmado!
        </h2>
        
        <p style={{ 
          color: '#6b7280', 
          fontSize: '16px', 
          lineHeight: '1.6',
          margin: '0 0 8px 0'
        }}>
          Tu documento ha sido firmado exitosamente.
        </p>
        
        <p style={{ 
          color: '#374151', 
          fontSize: '15px', 
          fontWeight: '600',
          margin: '0 0 25px 0'
        }}>
          📧 Recibirás una copia en tu correo electrónico.
        </p>
        
        {/* Barra de progreso animada */}
        <div style={{
          width: '100%', height: '6px', backgroundColor: '#e5e7eb',
          borderRadius: '3px', overflow: 'hidden'
        }}>
          <div style={{
            height: '100%', 
            background: 'linear-gradient(90deg, #10b981, #34d399)',
            animation: 'progressBar 3s linear forwards',
            borderRadius: '3px'
          }}></div>
        </div>
        
        <p style={{ 
          color: '#9ca3af', 
          fontSize: '13px', 
          marginTop: '15px',
          margin: '15px 0 0 0'
        }}>
          Redirigiendo automáticamente...
        </p>
      </div>
    </div>
  );

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes scaleIn {
            from { 
              opacity: 0; 
              transform: scale(0.8) translateY(20px); 
            }
            to { 
              opacity: 1; 
              transform: scale(1) translateY(0); 
            }
          }
          
          @keyframes bounceIn {
            0% { 
              opacity: 0; 
              transform: scale(0.3); 
            }
            50% { 
              transform: scale(1.1); 
            }
            70% { 
              transform: scale(0.9); 
            }
            100% { 
              opacity: 1; 
              transform: scale(1); 
            }
          }
          
          @keyframes drawCheck {
            from { 
              stroke-dashoffset: 30; 
            }
            to { 
              stroke-dashoffset: 0; 
            }
          }
          
          @keyframes progressBar {
            from { width: 0%; }
            to { width: 100%; }
          }
        `}
      </style>

      <div style={{
        maxWidth: '800px', margin: '20px auto', padding: '20px',
        backgroundColor: '#f8f9fa', borderRadius: '10px'
      }}>
        {/* Logos */}
        <div className="d-flex justify-content-center align-items-center mb-4">
          <img src="/logo2.png" alt="Logo Empresa" style={{ width: '150px', marginRight: '20px' }} />
          <img src="/logo1.png" alt="Logo Proyecto" style={{ width: '80px' }} />
        </div>

        <h2 className="text-center mb-4">Firma del Documento</h2>

        {/* Selección de modo */}
        <div className="text-center mb-4">
          <label className="fw-bold">Selecciona el tipo de firma:</label><br />
          <div className="form-check form-check-inline">
            <input type="radio" name="mode" value="hand" checked={mode === 'hand'}
              onChange={() => setMode('hand')} className="form-check-input" disabled={isProcessing} />
            <label className="form-check-label">A Mano Alzada</label>
          </div>
          <div className="form-check form-check-inline">
            <input type="radio" name="mode" value="typed" checked={mode === 'typed'}
              onChange={() => setMode('typed')} className="form-check-input" disabled={isProcessing} />
            <label className="form-check-label">Con Texto</label>
          </div>
        </div>

        {/* Área de firma */}
        {mode === 'hand' ? (
          <div className="text-center">
            <canvas ref={canvasRef} style={{
              border: '2px solid #1a1e4b', borderRadius: '10px',
              backgroundColor: 'white', display: 'block', margin: '0 auto',
              maxWidth: '100%', cursor: 'crosshair', touchAction: 'none'
            }} />
            <button onClick={clearCanvas} className="btn btn-secondary mt-2" disabled={isProcessing}>
              Limpiar Firma
            </button>
          </div>
        ) : (
          <div className="text-center">
            <input type="text" className="form-control mb-3" style={{ maxWidth: '400px', margin: '0 auto' }}
              placeholder="Escribe tu nombre completo" value={typedName}
              onChange={(e) => setTypedName(e.target.value)} disabled={isProcessing} />
            <div style={{
              fontFamily: '"Dancing Script", cursive', fontSize: '36px',
              border: '2px dashed #ccc', padding: '10px', borderRadius: '10px',
              backgroundColor: 'white', maxWidth: '400px', margin: '0 auto'
            }}>
              {typedName || 'Tu firma aquí'}
            </div>
          </div>
        )}

        <div className="text-center mt-4">
          <button onClick={handleSubmit} className="btn btn-primary px-5 py-2" disabled={isProcessing}>
            {isProcessing ? 'Procesando...' : 'Finalizar Firma'}
          </button>
        </div>
      </div>

      {isProcessing && <LoadingPopup />}
      {showSuccess && <SuccessPopup />}
    </>
  );
}

export default SignatureCapturePage;